import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { shopService, orderService } from '../services';
import VoiceRecorder from '../components/VoiceRecorder';
import AdCarousel from '../components/AdCarousel';

const OrderPage = () => {
  const { shopId } = useParams();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'voice', 'text', 'owner'
  const [orderText, setOrderText] = useState('');
  const [parsedOrder, setParsedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en-IN');
  const [adImages, setAdImages] = useState([]);

  useEffect(() => {
    loadShopData();
    loadAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const loadShopData = async () => {
    try {
      const response = await shopService.getShopById(shopId);
      if (response.success) {
        setShop(response.shop);
      }
    } catch (err) {
      setError('Failed to load shop details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAds = async () => {
    try {
      const response = await shopService.getShopAds(shopId);
      if (response.success) {
        setAdImages(response.adImages || []);
      }
    } catch (err) {
      console.error('Error loading ads:', err);
    }
  };

  const handleVoiceTranscription = (transcription) => {
    // Append new transcription to existing order text
    const updatedText = orderText ? `${orderText}, ${transcription}` : transcription;
    setOrderText(updatedText);
    parseOrder(updatedText);
  };

  const parseOrder = async (text) => {
    if (!text.trim()) return;

    try {
      const response = await orderService.parseOrder(shopId, text, selectedLanguage);
      if (response.success) {
        setParsedOrder(response);
      }
    } catch (err) {
      console.error('Parse error:', err);
      setError('Failed to parse order');
    }
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setOrderText(text);

    // Debounce parsing
    if (window.parseTimeout) {
      clearTimeout(window.parseTimeout);
    }
    window.parseTimeout = setTimeout(() => {
      parseOrder(text);
    }, 500);
  };

  const handleSubmitOrder = async () => {
    if (!parsedOrder || parsedOrder.parsedItems.length === 0) {
      setError('Please add items to your order');
      return;
    }

    if (!customerName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!customerMobile.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await orderService.createOrder({
        shopId,
        orderText,
        orderItems: parsedOrder.parsedItems,
        customerName: customerName.trim(),
        customerMobile: customerMobile.trim(),
        createdBy: mode === 'owner' ? 'shop-owner' : 'customer',
        selectedLanguage: selectedLanguage
      });

      if (response.success) {
        setSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setMode(null);
          setOrderText('');
          setParsedOrder(null);
          setSuccess(false);
          setCustomerName('');
          setCustomerMobile('');
        }, 3000);
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.error || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!shop) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Shop not found</div>;
  }

  if (!shop.isActive) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>This shop is currently inactive</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>{shop.shopName}</h1>
      <p>{shop.address}</p>

      {/* Ad Banner Carousel */}
      {adImages.length > 0 && <AdCarousel images={adImages} />}

      {success && (
        <div style={{
          padding: '15px',
          backgroundColor: '#4CAF50',
          color: 'white',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          Order placed successfully!
        </div>
      )}

      {error && (
        <div style={{
          padding: '15px',
          backgroundColor: '#f44336',
          color: 'white',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {!mode && (
        <div>
          <h2>How would you like to order?</h2>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '20px' }}>
            <button
              onClick={() => setMode('voice')}
              style={{
                padding: '20px 30px',
                fontSize: '18px',
                backgroundColor: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                flex: '1',
                minWidth: '200px'
              }}
            >
              🎤 Speak Order
            </button>
            <button
              onClick={() => setMode('text')}
              style={{
                padding: '20px 30px',
                fontSize: '18px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                flex: '1',
                minWidth: '200px'
              }}
            >
              ✍️ Type Order
            </button>
            <button
              onClick={() => setMode('owner')}
              style={{
                padding: '20px 30px',
                fontSize: '18px',
                backgroundColor: '#9C27B0',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                flex: '1',
                minWidth: '200px'
              }}
            >
              👨‍💼 Shop Owner Entry
            </button>
          </div>
        </div>
      )}

      {mode && (
        <div style={{ marginTop: '30px' }}>
          <button
            onClick={() => {
              setMode(null);
              setOrderText('');
              setParsedOrder(null);
              setCustomerName('');
              setCustomerMobile('');
            }}
            style={{
              padding: '10px 20px',
              marginBottom: '20px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>

          <h2>
            {mode === 'voice' && '🎤 Voice Order'}
            {mode === 'text' && '✍️ Type Your Order'}
            {mode === 'owner' && '👨‍💼 Shop Owner Entry'}
          </h2>

          {mode === 'voice' && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                  Select Language:
                </label>
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '16px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="en-IN">🇮🇳 English (India)</option>
                  <option value="hi-IN">🇮🇳 हिंदी (Hindi)</option>
                  <option value="ta-IN">🇮🇳 தமிழ் (Tamil)</option>
                </select>
              </div>
              <VoiceRecorder
                onTranscriptionComplete={handleVoiceTranscription}
                languageCode={selectedLanguage}
              />
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Order Text:
            </label>
            <textarea
              value={orderText}
              onChange={handleTextChange}
              placeholder="e.g., 2 kg rice, 1 kg sugar, half kg dal"
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
            <small>Example: "2 kg rice, 1 kg sugar, half liter oil"</small>
          </div>

          {/* Customer Information - Now shown for all modes */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Customer Name *
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Enter your name"
              required
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Phone Number *
            </label>
            <input
              type="tel"
              value={customerMobile}
              onChange={(e) => setCustomerMobile(e.target.value)}
              placeholder="+91XXXXXXXXXX"
              required
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          {parsedOrder && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px'
            }}>
              <h3>Order Preview</h3>

              {parsedOrder.parsedItems.length > 0 ? (
                <table style={{ width: '100%', marginTop: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Product</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>Quantity</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>Price/Unit</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedOrder.parsedItems.map((item, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px' }}>{item.productName}</td>
                        <td style={{ textAlign: 'right', padding: '8px' }}>
                          {item.quantity} {item.unit}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px' }}>
                          ₹{item.pricePerUnit}
                        </td>
                        <td style={{ textAlign: 'right', padding: '8px' }}>
                          ₹{item.itemTotal.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                        Total:
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px', fontWeight: 'bold' }}>
                        ₹{parsedOrder.totalAmount.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <p>No items found. Please check your order text.</p>
              )}

              {parsedOrder.validationErrors && parsedOrder.validationErrors.length > 0 && (
                <div style={{ color: 'red', marginTop: '10px' }}>
                  <strong>Errors:</strong>
                  <ul>
                    {parsedOrder.validationErrors.map((err, index) => (
                      <li key={index}>{err.item}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={handleSubmitOrder}
                disabled={submitting || parsedOrder.parsedItems.length === 0}
                style={{
                  marginTop: '15px',
                  padding: '12px 30px',
                  fontSize: '16px',
                  backgroundColor: submitting ? '#ccc' : '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {submitting ? 'Placing Order...' : 'Confirm Order'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPage;
